import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import Link from 'next/link'
import type { Ref } from 'react'
import React from 'react'
import { useAllProductIds } from '../../../hooks/useAllProductIds'
import { useIsAllowedToFullUse } from '../../../hooks/useIsAllowedToFullUse'
import { useTranslate } from '../../contexts/TranslationContext'
import { CompareButton } from '../shared/CompareButton'
import { Tag } from '../shared/Tag'
import style from './Product.module.sass'

type ProductPreviewProps = {
	title: string
	name?: string
	url: string
	isBeingCompared?: boolean
	imageUrl?: string
	id: string
	isMinimalized?: boolean
	ref?: Ref<HTMLDivElement>
	openModal?: () => void
}

export const ProductPreview = React.memo(function ProductPreview(props: ProductPreviewProps) {
	const translate = useTranslate()

	const allProductsStats = useAllProductIds(props.id)

	const indexNumber =
		typeof allProductsStats.productIndex === 'number' && allProductsStats.productIndex >= 0
			? allProductsStats.productIndex + 1
			: undefined

	const isAllowedToFullUse = useIsAllowedToFullUse()

	const label =
		indexNumber && indexNumber <= 3 ? `${translate('productIntroSummary.tag.top')} ${indexNumber}` : `${indexNumber}.`

	const content = (
		<div
			className={clsx(
				style.cardWrapper,
				props.isBeingCompared && style.cardWrapperCompared,
				props.imageUrl && style.cardWrapperHasImage,
				props.isMinimalized && style.cardWrapperMinimalized,
			)}
		>
			<div className={clsx(style.tagWrapper, props.isMinimalized && style.tagWrapperMinimalized)}>
				{indexNumber && (isAllowedToFullUse || allProductsStats.isFed || allProductsStats.isTopFirst) && (
					<Tag
						color={indexNumber <= 3 ? 'greenLight' : allProductsStats.isFed ? 'pink' : 'grey'}
						isLarger
						label={
							props.isMinimalized
								? `${indexNumber}.`
								: allProductsStats.isFed
									? `${translate('productBanner.tag.fedProduct')}, ${indexNumber}.`
									: label
						}
					/>
				)}
			</div>
			<Link href={props.url} title={props.title}>
				{props.imageUrl && (
					<div
						className={clsx(
							style.imageWrapper,
							props.isBeingCompared && style.imageWrapperCompared,
							!props.isBeingCompared && style.imageWrapperCarousel,
						)}
					>
						<NextImage
							src={props.imageUrl}
							alt={translate('alternativeText.productImage')}
							layout="fill"
							priority={props.isBeingCompared}
							objectFit="contain"
						/>
					</div>
				)}
				{!props.isBeingCompared && (
					<h3 className={clsx(style.title, props.isBeingCompared && !props.isMinimalized && style.titleCompared)}>
						{props.title} <p className={style.lead}>{props.name}</p>
					</h3>
				)}
			</Link>
			{!props.isBeingCompared && <CompareButton id={props.id} isLeftAligned openModal={props.openModal} />}
		</div>
	)

	return props.ref ? (
		<>
			<div ref={props.ref} />
			{content}
		</>
	) : (
		content
	)
})
